export type CostContextAnalyticsProperties = {
  current_bid_usd: number | null
  minimum_required_bid_usd: number | null
  bstock_fee_usd: number | null
  total_landed_cost_usd: number | null
}

/**
 * Shared cost-context properties spread onto every LDP impression/
 * bid/shipping-quote analytics event, so each one carries the buyer's full
 * cost picture at that moment.
 *
 * Conventions (resolved with stakeholders 2026-08-20):
 * - Units are `_usd` (dollars) throughout, matching the existing
 *   `bid_amt_usd`/`total_landed_cost_usd` properties.
 * - `total_landed_cost_usd` is always the tax-inclusive figure, matching what
 *   the buyer is shown on screen (same convention as `useTlcTotals`).
 * - `bstock_fee_usd` is derived from the current bid, never the buyer's
 *   not-yet-submitted entered amount, on every surface including raise-bid
 *   flows.
 * - Any argument that isn't available yet (quote unresolved, surface has no
 *   quote context at all) maps to `null`, never an omitted property — every
 *   event carries the same four keys.
 */
export const getCostContextAnalyticsProperties = ({
  currentBidUsd,
  minimumRequiredBidUsd,
  bstockFeeCents,
  totalLandedCostUsd,
}: {
  currentBidUsd: number | null | undefined
  minimumRequiredBidUsd: number | null | undefined
  /**
   * bstockFee in cents, e.g. from `CalculatedAdditionalCharges.bstockFee`
   */
  bstockFeeCents: number | null | undefined
  totalLandedCostUsd: number | null | undefined
}): CostContextAnalyticsProperties => ({
  current_bid_usd: currentBidUsd ?? null,
  minimum_required_bid_usd: minimumRequiredBidUsd ?? null,
  bstock_fee_usd: bstockFeeCents == null ? null : bstockFeeCents / 100,
  total_landed_cost_usd: totalLandedCostUsd ?? null,
})
